import { z } from 'zod'
import { lenient, lenientNullable, timestampToDate, timestampToDateNullable } from '@upadelmatch/core'

// Match document — `matches/{matchId}` (docs/data-model.md § matches, the
// frozen legacy contract) plus two documented rebuild additions:
//  - `positionSought` — the validated pre-launch "Busco derecha/revés" facet
//    (features.md); stored as the platform side enum, null = Indiferente
//    (an empty filter, mirroring the levelMin/levelMax both-null convention).
//  - `playersInfo` — denormalized display map {uid: {name, photoURL}}. The
//    feed must render player names but `users/{uid}` is owner-readable only
//    (privacy rule), so the display facts ride the match doc — written at
//    create for the organizer, maintained on join/leave.

export const MATCH_TYPES = ['casual', 'competitive'] as const
export const MATCH_PRIVACIES = ['public', 'private'] as const
/**
 * PERSISTED status. There is a SECOND, different status vocabulary in the view
 * model (`shared/mock/data.ts`): `active | full | past | cancelled`. The
 * mismatch is deliberate and the two must not be "aligned".
 *
 *   persisted:  active · full · cancelled · completed
 *   view model: active · full · cancelled · past
 *
 * `past` is DERIVED, never stored — `use-matches.ts` maps a doc to it purely by
 * clock (`startDateTime < now`), which is why `completed` has no view-model twin:
 * a result can only be recorded after kickoff (`isRecordResult` and the
 * organizer branch's `resultOnlyAfterKickoff()` both enforce it), so every
 * `completed` match is already `past` by time. The UI separates "played, result
 * in" from "time passed, nobody recorded" on `result != null`, not on status.
 *
 * Recorded 2026-08-11 because the divergence reads exactly like drift, and
 * "correcting" the mock type to add `completed` / drop `past` would break
 * match-card, match-detail-page, use-mis-partidos and funnel.ts at once.
 *
 * ⚠️ `full` is DERIVED TOO — it means precisely `availableSlots == 0`, and the
 * pair is enforced server-side by `statusMatchesSlots()` in firestore.rules.
 * Anything writing one without the other makes a match vanish from the feed,
 * which filters on `status === 'active' && availableSlots > 0`.
 */
export const MATCH_STATUSES = ['active', 'cancelled', 'completed', 'full'] as const
export const MATCH_POSITIONS = ['drive', 'reverse'] as const
/**
 * Identity ladder for a seat, weakest first. ORDER IS LOAD-BEARING — the
 * accountability rule is a comparison on this array, not a switch:
 * a seat at `guest` or above answers for its own no-show; a `placeholder`
 * never consented to anything, so its no-show belongs to `createdBy`.
 */
export const SEAT_IDENTITIES = ['placeholder', 'guest', 'account'] as const
export type SeatIdentity = (typeof SEAT_IDENTITIES)[number]
export const MATCH_GENDER_CATEGORIES = ['open', 'men', 'women', 'mixed'] as const

/**
 * The two values `users/{uid}.padel.gender` is compared against in the rules
 * today (`== 'female'`, `== 'male'`). Named here so the seat-level copy and the
 * profile-level source cannot drift into different vocabularies — a mixto match
 * counting 'M' against a profile saying 'male' would enforce nothing while
 * looking correct.
 */
export const SEAT_GENDERS = ['male', 'female'] as const
export type SeatGender = (typeof SEAT_GENDERS)[number]

/** Mixto is exactly 2+2, so no gender may exceed this. */
export const MIXED_MAX_PER_GENDER = 2

/** Padel court capacity — fixed at 4; companions = 4 − players − availableSlots. */
export const MATCH_CAPACITY = 4

export const MatchSchema = z.object({
  id: z.string(),
  /** Padel place name (denormalized display). */
  location: z.string().nullable().optional(),
  /** Manual address (when no club was picked). */
  address: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  country: z.string().nullable().optional(),
  padelPlaceId: z.string().nullable().optional(),
  countryCode: z.string().nullable().optional(),
  cityKey: z.string().nullable().optional(),
  latitude: z.number().nullable().optional(),
  longitude: z.number().nullable().optional(),
  geohash: z.string().nullable().optional(),
  startDateTime: timestampToDate,
  durationInMinutes: z.number().int().default(90),
  type: lenient(z.enum(MATCH_TYPES), 'casual', 'match.type').default('casual'),
  /** Required nivel range 0–7; both null = "Indiferente" (any level). */
  levelMin: z.number().min(0).max(7).nullable().optional(),
  levelMax: z.number().min(0).max(7.01).nullable().optional(),
  positionSought: lenientNullable(z.enum(MATCH_POSITIONS), 'match.positionSought'),
  /**
   * Who can join (Playtomic-aligned axis, decision 2026-07-05): 'open' is the
   * default — a cold-start market must never fragment by default. 'mixed'
   * (paired man+woman teams) is schema-ready but UI/enforcement land with the
   * ship-2 join CF.
   */
  genderCategory: lenient(z.enum(MATCH_GENDER_CATEGORIES), 'open', 'match.genderCategory').default('open'),
  /** Player UIDs — organizer included. */
  players: z.array(z.string()).default([]),
  /** Display facts for `players` (users docs are owner-readable only). */
  playersInfo: z
    .record(
      z.string(),
      z.object({
        name: z.string(),
        photoURL: z.string().nullable().optional(),
        /**
         * How much proof of identity this seat carries — a LADDER, not three
         * unrelated cases (spec/02 § Seats & identity):
         *   placeholder → guest → account
         * Each rung is the same act: proving more about a seat you already
         * hold. `phone_verified` slots in as another rung when #21 lands.
         *
         * Optional and lenient because every match written before this field
         * existed has none: absent means `guest === true ? 'guest' : 'account'`
         * (see `seatIdentity`). Reading a legacy doc must never fail — that is
         * the same lenient() discipline the rest of this schema uses.
         */
        identity: z.enum(SEAT_IDENTITIES).optional(),
        /**
         * PLACEHOLDER ONLY — the uid that asserted this person would come, and
         * therefore the uid a no-show counts against. Never self-assigned; the
         * rules enforce that exactly like `levelSource: 'computed'`.
         */
        createdBy: z.string().optional(),
        /**
         * MIXTO ONLY — the gender this seat occupies, so `genderCategory:
         * 'mixed'` can mean 2+2 instead of nothing at all.
         *
         * Until 2026-08-11 `mixed` existed in this enum, was accepted by the
         * rules (`cat == 'open' || cat == 'mixed'` → eligible), and could not
         * be created by anything in the app. Had it been reachable it would
         * have enforced NOTHING: four men could fill a match labelled "Mixto".
         *
         * WHY IT LIVES HERE rather than being read from `users/{uid}`. Counting
         * the seated genders from user docs costs one `get()` per seated player
         * on every join — four document reads to admit one person, against a
         * hard rules budget. Denormalising it onto the seat the player already
         * writes for themselves costs zero extra reads.
         *
         * Trust is unchanged: `men`/`women` already gate on the same
         * self-declared `padel.gender`, and `ownPlayersInfoOnly()` stops a
         * player writing anyone's entry but their own.
         *
         * PLACEHOLDERS carry it too — a companion seated by name is half the
         * roster of a mixto match, and a seat with no gender cannot be counted.
         * A claim must then MATCH the seat's declared gender, or Javi claims
         * Ana's seat and the match is 3+1 with every field looking correct.
         *
         * Optional and lenient: every match written before this field existed
         * has none, and a missing gender must never block a join on a legacy
         * doc. Absent counts as unknown, never as a free slot.
         */
        gender: z.enum(SEAT_GENDERS).optional(),
        /** @deprecated superseded by `identity`; kept so legacy docs parse. */
        guest: z.boolean().optional(),
      }),
    )
    .default({}),
  availableSlots: z.number().int().min(0).max(MATCH_CAPACITY),
  organizer: z.string(),
  cost: z.number().nullable().optional(),
  /** Organizer's WhatsApp group invite (spec/02, roadmap #11) — members-only
   *  coordination bridge; validated to chat.whatsapp.com at write time. */
  whatsappGroupUrl: z.string().nullable().optional(),
  privacy: lenient(z.enum(MATCH_PRIVACIES), 'public', 'match.privacy').default('public'),
  status: lenient(z.enum(MATCH_STATUSES), 'active', 'match.status').default('active'),
  description: z.string().nullable().optional(),
  /** Stage-1 booking model: court booked MANUALLY outside the app. */
  courtReserved: z.boolean().default(false),
  /**
   * Recorded outcome (spec/usersmatch/05). Written ONCE by any player after
   * startDateTime (organizer may correct); recording flips status to
   * 'completed'. `seats` is the FLAT on-court arrangement (Firestore forbids
   * nested arrays): seats[0..1] = pair A, seats[2..3] = pair B; null = a seat
   * held by an off-app companion. `levelDeltas` is stamped later by the
   * calibration CF (competitive matches only), never by clients.
   */
  result: z
    .object({
      seats: z.array(z.string().nullable()).length(4),
      pairingIndex: z.number().int().min(0).max(2).default(0),
      winnerPair: z.union([z.literal(0), z.literal(1)]),
      recordedBy: z.string(),
      recordedAt: timestampToDateNullable,
      /** Seated app players flagged absent (spec/06) — recorder sets at
       *  recording, organizer may correct; CF counts them once. */
      noShows: z.array(z.string()).optional(),
      levelDeltas: z.record(z.string(), z.number()).optional(),
    })
    .nullable()
    .optional(),
  /** 1h-reminder dedup stamp — set once by the reminder sweep (R1). */
  reminderSentAt: timestampToDateNullable,
  createdAt: timestampToDateNullable,
  updatedAt: timestampToDateNullable,
})

export type MatchDoc = z.output<typeof MatchSchema>
